import { Extension } from '@tiptap/core'
import { Node } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import twitterText from 'twitter-text'
import {
  Matchers,
  SocialLinkDecoratorOptions,
  SocialLinkDecoratorStorage,
  SocialLinkMatch,
} from './types'

const availableMatchers: Matchers = {
  hashtag: new RegExp(twitterText.regexen.validHashtag, 'gu'),
  socialMention: new RegExp(twitterText.regexen.validMentionOrList, 'gu'),
  // https://uibakery.io/regex-library/url
  url: new RegExp(
    /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)/,
    'gu'
  ),
}

/**
 * Traverses the document and finds matches & their positions using
 * the given matchers.
 */
const parseSocialLinks = (doc: Node, matchers: Partial<Matchers>) => {
  const matches: SocialLinkMatch[] = []

  doc.descendants((node, pos) => {
    if (node.isText) {
      for (const [type, regExp] of Object.entries(matchers)) {
        let match
        while ((match = regExp.exec(node.text ?? ''))) {
          matches.push({
            type: type as keyof Matchers,
            from: pos + match.index,
            to: pos + match.index + match[0].length,
          })
          // URL can only match once
          if (type === 'url') {
            break
          }
        }
      }
    }
  })

  return matches
}

/**
 * Applies class-based inline decorations based on the parsed social links.
 */
const applyDecorations = (
  doc: Node,
  matchers: Partial<Matchers>,
  className: string
) => {
  const decorations = []
  for (const match of parseSocialLinks(doc, matchers)) {
    decorations.push(
      Decoration.inline(match.from, match.to, { class: className })
    )
  }

  return DecorationSet.create(doc, decorations)
}

declare module '@tiptap/core' {
  interface Storage {
    socialLinkDecorator: SocialLinkDecoratorStorage
  }
}

/**
 * A plugin to apply decorator classes to social links, such as
 * hashtags, mentions, and URLs.
 */
export const SocialLinkDecorator = Extension.create<
  SocialLinkDecoratorOptions,
  SocialLinkDecoratorStorage
>({
  name: 'socialLinkDecorator',

  // Default options
  addOptions() {
    return {
      matchOn: ['hashtag', 'socialMention', 'url'],
      className: 'social-link-match',
    }
  },

  addStorage() {
    return {
      enabledMatchers: Object.fromEntries(
        this.options.matchOn.map((key) => [key, availableMatchers[key]])
      ),
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: new PluginKey('socialLinkDecorator'),
        state: {
          // Apply decorations when document is initialized
          init: (_, { doc }) =>
            applyDecorations(
              doc,
              this.storage.enabledMatchers,
              this.options.className
            ),
          // And apply when the document changes
          apply: (tr, old) =>
            tr.docChanged
              ? applyDecorations(
                  tr.doc,
                  this.storage.enabledMatchers,
                  this.options.className
                )
              : old,
        },
        props: {
          decorations(state) {
            return this.getState(state)
          },
        },
      }),
    ]
  },
})

export type { SocialLinkDecoratorOptions, SocialLinkDecoratorStorage }
